import { useEffect } from 'react';
import store from "../../../../store";
import workspace from "../../components/canvas/core/workspace";
import sqftShapeManager from "../../components/canvas/store/sqftShapeManager";

/**
 * Applies the feature the user has just picked to the sqft shapes of the cart item being edited.
 *
 * Three different "features" are involved here, it is easy to mix them up:
 *
 * 1) cartState.cartItemEditFeature - the cart item the user pressed "Edit" on in the cart.
 *    Holds the OLD selection (what is currently saved in the quote). Used here to find
 *    which shapes belong to that cart item.
 *
 * 2) selectionState.activeFeature - the feature currently applied to the VISUALIZER layout.
 *    It is NOT necessarily what the user picked: for price-level features (e.g. "LEVEL 07")
 *    the layout keeps showing the real stone (e.g. "Alpine Mist"), so activeFeature stays on
 *    the old material. Relying on it is what caused QCDES-8812 (Level 7 reverting to Alpine Mist).
 *
 * 3) selectionState.sqftOpenedFromFeature - the feature the calculator was actually opened from
 *    (row list item / feature info screen / cart item). This is the user's real pick, so it is
 *    the one we write into the shapes.
 *
 * Do not confuse (3) with sqftState.sqftFromFeature - despite the similar name they are different:
 *  - sqftFromFeature is an INPUT PARAMETER of the whole calculator: it is a cart-item shaped object
 *    ({cartFeature, quantity, ...}), it decides the featureId of NEWLY created shapes
 *    (see sqft/helpers/featureHelper.js), the active label/navigation (shapes/shapeFunctions.js)
 *    and the feature matching in parseSqftData. When entering from the cart it is deliberately set
 *    to cartItemEditFeature, i.e. it carries the OLD feature plus the cart data.
 *  - sqftOpenedFromFeature is a plain feature and answers exactly one question: "which feature did
 *    the user click to get here". It is only used here, so changing it is safe.
 */

// shape id(s) this cart item was drawn as - set on cartItem.sqftId/sqftIds when the item is added
// from the SQFT calculator (see parseSqftData.js, "sqftId: featurePassed.id" - featurePassed.id is
// a workspace shape id, same as the id sqftShapeManager.setShapeFeature expects). This is more
// reliable than the item's featureId: it can't collide with another item (unlike featureId, which
// can be identical for two different areas sharing the same material), and it still resolves even
// if the shape's featureId has drifted out of sync with the cart (broken/legacy quotes).
const getLinkedShapeIds = (cartItem) => {
  const ids = Array.isArray(cartItem?.sqftIds) ? cartItem.sqftIds : [];
  return (cartItem?.sqftId === undefined || cartItem?.sqftId === null)
    ? ids
    : [...ids, cartItem.sqftId];
};

const useUpdateEditedFeatures = () => {

  //modify backsplash, countertops & edges on edit from cart click
  useEffect (() => {
    const state = store.getState();
    const {cartItemEditFeature} = state.cartState;
    const {activeFeature, sqftOpenedFromFeature} = state.selectionState;

    // fall back to activeFeature to keep the old behaviour for entry points that do not set it yet
    const featureToApply = sqftOpenedFromFeature || activeFeature;

    if (!cartItemEditFeature || !featureToApply || cartItemEditFeature.partId !== featureToApply.partId) {
      return;
    }

    // 1.) Update linked shapes by sqft id
    const linkedShapes = getLinkedShapeIds(cartItemEditFeature)
      .map(shapeId => workspace.getShapeById(shapeId))
      .filter(Boolean);

    if (linkedShapes.length) {
      for (const shape of linkedShapes) {
        sqftShapeManager.setShapeFeature(shape.id, featureToApply);
      }
      sqftShapeManager.updateShapesWizardProperties();
      return;
    }

    // 2.) Update linked shapes by feature id
    // Fallback for cart items with no sqftId (not added from the calculator, e.g. via
    // getCartItemObject()) - locate shapes by feature id, same behaviour as before.
    const sqftFeaturesByPartId = workspace.getShapesByPartId(featureToApply.partId);
    if (sqftFeaturesByPartId && sqftFeaturesByPartId.length) {
      //include slab shapes
      const sqftFeaturesByFeatureId = workspace.getShapesByFeatureId(sqftFeaturesByPartId[0].featureId);
      for (const sqftFeature of sqftFeaturesByFeatureId) {
        // only shapes that still hold the edited cart item feature, nothing else is touched.
        if (sqftFeature.featureId === cartItemEditFeature?.cartFeature.dbFeature?.id) {
          sqftShapeManager.setShapeFeature(sqftFeature.id, featureToApply);
          sqftShapeManager.updateShapesWizardProperties();
        }
      }
    }
  }, []);
};

export default useUpdateEditedFeatures;
